import type { DemoDoc, Step } from '@mado/schema';
import { buildPicture } from './media';
import { drawnAssetId } from './sizing';

/**
 * Asset prefetching.
 *
 * "Zero stall on step advance" is a CI-enforced budget (PRODUCT-PLAN §5.4). The
 * JS is 60KB; the media is megabytes — so this file, not the bundle size, is what
 * actually decides whether the player feels instant. A step whose picture is not
 * here when it is entered shows its placeholder — a ~1KB blur — until the file
 * arrives, stretched to the zoom the step is drawn at.
 *
 * Branching means "the next step" is a set, not a single step, so we walk the
 * reachable graph breadth-first to a shallow depth, from every step entered.
 */

const DEPTH = 2;

/** Steps reachable from `from` within `depth` hops. */
export function reachable(doc: DemoDoc, from: Step, depth = DEPTH): Step[] {
  const byId = new Map(doc.steps.map((s) => [s.id, s]));
  const seen = new Set<string>([from.id]);
  const out: Step[] = [];
  let frontier: Step[] = [from];

  for (let d = 0; d < depth; d++) {
    const next: Step[] = [];
    for (const s of frontier) {
      const targets = [...s.hotspots.map((h) => h.next ?? s.next), s.next];
      for (const id of targets) {
        if (!id || seen.has(id)) continue;
        const step = byId.get(id);
        if (!step) continue;
        seen.add(id);
        out.push(step);
        next.push(step);
      }
    }
    frontier = next;
    if (!frontier.length) break;
  }
  return out;
}

export class Prefetcher {
  private done = new Set<string>();

  constructor(
    private doc: DemoDoc,
    /** The `sizes` width a step's stage will use (sizing.ts `stepSizes`), read at warm time so a resize is honoured. */
    private sizesFor: (step: Step) => number,
    private assetBase?: string | null,
  ) {}

  /** Warm everything reachable from this step. Idempotent and fire-and-forget. */
  warm(step: Step): void {
    for (const s of reachable(this.doc, step)) {
      // Only the asset the stage draws: `assetSm` is never shown, and a
      // scroll step shows its stitched `assetTall`, not `asset`.
      const id = drawnAssetId(s);
      const asset = this.doc.assets[id];
      if (!asset) continue;
      const sizes = Math.round(this.sizesFor(s));
      const key = `${id}@${sizes}`;
      if (this.done.has(key)) continue;
      this.done.add(key);
      // The same <picture> the stage builds, so the browser makes the same
      // choice — codec and width — and the stage finds that exact file in
      // cache. decode() rather than onload: it forces the raster work to
      // happen now, off the step-advance critical path.
      const img = buildPicture(asset, sizes, this.assetBase).querySelector('img');
      void img?.decode?.().catch(() => {});
    }
  }
}
